import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import type {
  TireMetatags,
  TirePartItem,
} from "../../../../queries/useSearchTiresByVehicle";
import type { ModifiedTirePartItem } from "../../../Tires/Results/useTireResults";

import { partTypeTire } from "../../../../constants/partTypes";
import { useLanguage } from "../../../../i18n/useLanguage";
import { useIsLoggedOn } from "../../../../stores/hooks/useIsLoggedOn";
import { Image } from "../../../../ui/Image/Image";
import { fixTireImageUrl } from "../../../../util/fixTireImageUrl";
import { getPartFallbackSrc } from "../../../../util/getPartFallbackSrc";
import { getPartName } from "../../../../util/getPartName";
import { getTiresizeAndClass } from "../../../../util/getTireClassAndSize";
import { useStockIsEnabled } from "../../../Cart/useStockIsEnabled";
import { PriceSelectEntry } from "../../../PriceSelect/usePriceSelectState";
import { getTirePartNumber } from "../../../Tires/Common/getTirePartNumber";
import { Rebates } from "../../../Tires/Results/Tile/Rebates";
import { useRibbon } from "../../../Tires/Results/Tile/Ribbon/useRibbon";
import { TireStock, filterStock } from "../../../Tires/Results/Tile/TireStock";
import { TireMetatagList } from "../../../Tires/Results/TireMetatagList/TireMetatagList";
import { PartPrice } from "./PartPrice";

export const ComboTileTire: FunctionComponent<{
  index: number;
  metatagList: TireMetatags | null;
  onClick?: VoidFunction;
  part: ModifiedTirePartItem;
  priceOption: PriceSelectEntry[];
  quantity: number;
}> = ({ index, metatagList, onClick, part, priceOption, quantity }) => {
  const [language] = useLanguage();

  const ribbon = useRibbon({
    className: "tw-tire-tile__ribbon tw-tire-tile__ribbon--compact",
    index,
    part,
  });

  const { t } = useTranslation();
  const isLoggedOn = useIsLoggedOn();

  const allStock = part?.partStock.primaryPart.filter(({ stockQty }) => {
    const hasQty = Number(stockQty) > 0;
    return hasQty;
  });

  const stockIsEnabled = useStockIsEnabled();

  const shouldRenderStockData = Boolean(stockIsEnabled && allStock?.length);

  const dlrStock = filterStock(allStock || [], "DLR");
  const rltStock = filterStock(allStock || [], "RLT");

  return (
    <div
      className={clsx("combo__tile md:combo__tile--md lg:combo__tile--lg")}
      id={`combo-${part.partDetails.primaryPart.itemId}`}
      onClick={onClick}
    >
      <Rebates part={part} quantity={priceOption[0]?.pricingOption.quantity} />
      {ribbon}
      {part?.popular && (
        <div className="tw-tire-tile__popular-ribbon">
          {t("dealerTireTile.mostPopular")}
        </div>
      )}
      <h3 className="flex flex-wrap items-center gap-1">
        {getPartName(part, language)}
        <TireMetatagList
          tireCategoryTags={
            part?.partDetails.primaryPart.tireCategoryTags || []
          }
          metatagList={metatagList}
          runFlat={Number(part?.partDetails.primaryPart.tireRunFlat) === 1}
        />
      </h3>
      <div className="combo__tile_info">
        <div className="combo__tile_texts">
          <p>
            {getTiresizeAndClass(part as TirePartItem, false)},{" "}
            {part?.partDetails.primaryPart.tireLoadRating}/
            {part?.partDetails.primaryPart.tireSpeedIndex}/
            {part?.partDetails.primaryPart.tirePlyRating}
          </p>
          <p className="combo__tile_sub">{getTirePartNumber(part)}</p>

          <p className="combo__tile_price pb-2">
            <PartPrice part={part} quantity={quantity} />
          </p>
          <div>
            <img
              alt="Manufacturer logo"
              className="h-4"
              src={part.partDetails.primaryPart.tireManufacturerImage}
            />
          </div>
        </div>
        <div>
          <Image
            alt="product image"
            className="combo__tile_image"
            errorClassName="opacity-60 blur-[16px]"
            fallbackSrc={getPartFallbackSrc(partTypeTire)}
            src={fixTireImageUrl(part.partDetails.primaryPart.tireImageLink)}
          />
        </div>
      </div>
      {shouldRenderStockData && part && (
        <TireStock
          allStock={allStock!}
          dlrStock={dlrStock}
          dropdownId={`tire-stock-${part?.partDetails.primaryPart.itemId}`}
          isRestricted={!isLoggedOn && stockIsEnabled}
          part={part}
          rltStock={rltStock}
        />
      )}
    </div>
  );
};
